import { Policy } from "@/schemas/firestore/policy.schema";
import { useStore } from "@/store";
import { useCallback, useMemo } from "react";

export function usePolicies(orgId?: string, domainId?: string) {
  // Store selectors
  const allPolicies = useStore((state) => state.policies);
  const selectedPolicyId = useStore((state) => state.selectedPolicyId);
  const isLoading = useStore((state) => state.isLoading);
  const error = useStore((state) => state.error);

  // Store actions
  const subscribeToPolicies = useStore((state) => state.subscribeToPolicies);
  const setSelectedPolicy = useStore((state) => state.setSelectedPolicy);
  const addPolicy = useStore((state) => state.addPolicy);
  const updatePolicy = useStore((state) => state.updatePolicy);
  const deletePolicy = useStore((state) => state.deletePolicy);
  const clearPolicies = useStore((state) => state.clearPolicies);
  const unsubscribePolicies = useStore((state) => state.unsubscribePolicies);

  // Filter policies for current domain if domainId is provided
  const policies = useMemo(() => {
    if (!domainId) return allPolicies;
    return allPolicies.filter((p) => p.domain_id === domainId);
  }, [allPolicies, domainId]);

  // Computed values
  const selectedPolicy = useMemo(() => {
    return selectedPolicyId
      ? policies.find((policy) => policy.id === selectedPolicyId) || null
      : null;
  }, [policies, selectedPolicyId]);

  const enabledPolicies = useMemo(() => {
    return policies.filter((policy) => policy.is_enabled);
  }, [policies]);

  // Action methods with orgId and domainId bound
  const createPolicy = useCallback(
    async (
      policyData: Omit<
        Policy,
        "id" | "created_at" | "updated_at" | "created_by" | "updated_by" | "domain_id"
      >,
    ) => {
      if (!orgId || !domainId) {
        throw new Error("Organization ID and Domain ID are required");
      }
      return await addPolicy(orgId, domainId, policyData);
    },
    [addPolicy, orgId, domainId],
  );

  const editPolicy = useCallback(
    async (policyId: string, policyData: Partial<Policy>) => {
      if (!orgId || !domainId) {
        throw new Error("Organization ID and Domain ID are required");
      }
      await updatePolicy(orgId, domainId, policyId, policyData);
    },
    [updatePolicy, orgId, domainId],
  );

  const removePolicy = useCallback(
    async (policyId: string) => {
      if (!orgId || !domainId) {
        throw new Error("Organization ID and Domain ID are required");
      }
      await deletePolicy(orgId, domainId, policyId);
    },
    [deletePolicy, orgId, domainId],
  );

  const subscribe = useCallback(() => {
    if (!orgId || !domainId) {
      console.warn(
        "Cannot subscribe to policies: orgId and domainId are required",
      );
      return () => {};
    }
    return subscribeToPolicies(orgId, domainId);
  }, [subscribeToPolicies, orgId, domainId]);

  return {
    // Data
    policies,
    enabledPolicies,
    selectedPolicy,
    selectedPolicyId,

    // State
    isLoading,
    error,

    // Actions
    createPolicy,
    editPolicy,
    removePolicy,
    setSelectedPolicy,
    clearPolicies,
    subscribe,
    unsubscribe: unsubscribePolicies,
  };
}
